import { Button, Form, message } from 'antd';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { nullSafeGet } from '../../../utils/DataAccessUtils';
import { updateTroubleshootingGuideAssocations } from '../../../thunks/troubleshooting_guides_thunks';
import { withUserType } from '../../withUserType';
import TroubleshootingAssociationEditFields from './TroubleshootingAssociationEditFields';
import { withAssociationsOmittedWhenEmpty } from '../../../utils/TroubleshootingUtils';

const FORM_ID = 'ts-guide-associations-form';

// "Select All" is true only when the backend returned [] for that axis (wildcard). A missing key
// or null means the user never set anything for that axis, so the toggle stays off.
const deriveAllSelected = (guide, key) => {
	const value = nullSafeGet(key, guide);
	return Array.isArray(value) && value.length === 0;
};

const TroubleshootingAssicationsEditForm: FC<any> = ({
	onCancel,
	onSuccess,
	troubleshootingGuide,
	updateAssociations,
}): React.ReactElement => {
	const [form] = Form.useForm();

	const [saving, setSaving] = useState(false);

	const handleSubmit = useCallback(
		(values) => {
			setSaving(true);
			updateAssociations({
				id: troubleshootingGuide.id,
				...withAssociationsOmittedWhenEmpty(values),
			})
				.then(() => {
					message.success('Associations updated successfully!');
					onSuccess && onSuccess();
				})
				.catch(message.error)
				.finally(() => setSaving(false));
		},
		[onSuccess, troubleshootingGuide.id, updateAssociations]
	);

	const allProblemTypes = useMemo(
		() => deriveAllSelected(troubleshootingGuide, 'problemTypeIds'),
		[troubleshootingGuide]
	);

	const allLocations = useMemo(
		() => deriveAllSelected(troubleshootingGuide, 'locationIds'),
		[troubleshootingGuide]
	);

	const allWOPriorities = useMemo(
		() => deriveAllSelected(troubleshootingGuide, 'workOrderPriorityIds'),
		[troubleshootingGuide]
	);

	const initialValues = useMemo(
		() => ({
			...(troubleshootingGuide || {}),
			allProblemTypes,
			allLocations,
			allWOPriorities,
		}),
		[allLocations, allProblemTypes, allWOPriorities, troubleshootingGuide]
	);

	return (
		<Form
			layout="horizontal"
			onFinish={handleSubmit}
			initialValues={initialValues}
			id={FORM_ID}
			labelCol={{ span: 8 }}
			wrapperCol={{ span: 16 }}
			labelAlign="left"
			form={form}
		>
			<TroubleshootingAssociationEditFields form={form} />
			<Form.Item>
				<Button type="primary" htmlType="submit" loading={saving}>
					Apply Changes
				</Button>
				<Button onClick={onCancel} style={{ marginLeft: 16 }}>
					Cancel
				</Button>
			</Form.Item>
		</Form>
	);
};

const mapStateToProps = (state) => ({
	fetching: state.troubleshooting_guides.fetching,
	troubleshootingGuide: state.troubleshooting_guides.detail,
	currentUser: state.session.currentUser,
	locations: state.locations,
	problemTypes: state.problem_types,
	workOrderPriorities: state.work_order_priorities,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateAssociations: (entity) =>
		dispatch(updateTroubleshootingGuideAssocations(ownProps.userType)(entity)),
});

export default withUserType(
	withRouter<any, any>(
		connect(mapStateToProps, mapDispatchToProps)(TroubleshootingAssicationsEditForm as any)
	) as any
);
